import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

export const BannerSkeleton = () => {
  return (
    <SkeletonTheme baseColor="#f3f4f6" highlightColor="#ffffff">
      <div className="w-full flex-col justify-start items-start gap-6 inline-flex">
        <div className="relative w-full">
          <div className="overflow-hidden">
            <div className="flex h-[280px] overflow-x-hidden gap-6">
              <div className="flex-shrink-0 h-full w-full overflow-hidden">
                <Skeleton height={280} width="100%" borderRadius={6} />
              </div>
            </div>
          </div>
        </div>
        <div className="w-full h-0.5 relative bg-[#eeeeee]">
        </div>
      </div>
    </SkeletonTheme>
  );
};

export const CardSkeleton = ({ className = "" }) => {
  return (
    <SkeletonTheme baseColor="#f3f4f6" highlightColor="#ffffff">
      <div className={`aspect-[0.723] px-1.5 pt-1.5 pb-3 bg-white rounded-md border border-[#eeeeee] flex-col justify-start items-start gap-2 inline-flex ${className}`}>
        {/* Card image area - square aspect ratio */}
        <div className="aspect-[1] w-full relative bg-white border border-[#DDDDDD] rounded overflow-hidden">
          <Skeleton height="100%" borderRadius={4} style={{ lineHeight: 'unset'}} />
          {/* Brand/condition badge skeleton */}
          <div className="bottom-[4px] absolute w-full px-1 flex">
            <div className='px-1.5 py-[3px] bg-white rounded'>
              <Skeleton height={12} width={60} />
            </div>
          </div>
        </div>
        
        {/* Card content area */}
        <div className="self-stretch px-1.5 flex-col justify-end items-start flex flex-1">
          {/* Card name - 2 lines */}
          <div className="self-stretch text-sm">
            <Skeleton height={14} className="mb-1" />
            <Skeleton height={14} width="60%" />
          </div>
          <div className='flex-1' />
          {/* Price and orders */}
          <div className="self-stretch flex items-center gap-2">
            <Skeleton height={18} width={80} />
            <div className="flex-1 flex justify-end">
              <Skeleton height={14} width={60} />
            </div>
          </div>
        </div>
      </div>
    </SkeletonTheme>
  );
};

export const BrandSkeleton = () => {
  return (
    <SkeletonTheme baseColor="#f3f4f6" highlightColor="#ffffff">
      <div className="w-full flex items-center gap-4 p-2">
        <Skeleton height={13} width={30} />
        <Skeleton height={13} width={100} className="flex-1" />
      </div>
    </SkeletonTheme>
  );
};

export const BrandBannerSkeleton = () => {
  return (
    <SkeletonTheme baseColor="#f3f4f6" highlightColor="#ffffff">
      <div className="w-full h-[136px] relative">
        <Skeleton height={136} width="100%" borderRadius={6} />
        <div className="absolute left-6 top-6 w-[88px] h-[88px]">
          <Skeleton height={88} width={88} borderRadius={4} />
        </div>
        <div className="absolute left-36 top-12">
          <Skeleton height={24} width={200} />
        </div>
      </div>
    </SkeletonTheme>
  );
};

export const BrandCardsFiltersSkeleton = () => {
  return (
    <SkeletonTheme baseColor="#f3f4f6" highlightColor="#ffffff">
      <div className="flex justify-between items-center mb-8">
        <div className="flex items-center gap-4">
          <Skeleton height={32} width={192} borderRadius={18} />
        </div>
        <Skeleton height={32} width={290} borderRadius={18} />
      </div>
    </SkeletonTheme>
  );
};

export const BrandCardsSkeleton = () => {
  const isMaxScreen = window.innerWidth >= 1440;
  const cardsPerRow = isMaxScreen ? 6 : 5;
  const totalCards = cardsPerRow * 6;

  return (
    <SkeletonTheme baseColor="#f3f4f6" highlightColor="#ffffff">
      <div className="mb-12">
        <BrandCardsFiltersSkeleton />
        <div className="grid grid-cols-5 max:grid-cols-6 gap-6">
          {Array.from({ length: totalCards }, (_, i) => (
            <CardSkeleton key={i} />
          ))}
        </div>
      </div>
    </SkeletonTheme>
  );
};

export const CardInfoSkeleton = () => {
  return (
    <SkeletonTheme baseColor="#f3f4f6" highlightColor="#ffffff">
      <div className="w-full relative bg-white rounded-md border border-[#dddddd] p-6 mt-6">
        <div className="flex gap-8">
          <div className="w-[88px] h-[88px] shrink-0">
            <Skeleton height={88} width={88} borderRadius={4} />
          </div>
          <div className="flex flex-col">
            <Skeleton height={24} width={50} className="mb-1" />
            <Skeleton height={20} width={500} containerClassName='leading-none' className="mb-1" />
            <Skeleton height={20} width={150} containerClassName='leading-none' className="mb-1" />
            <Skeleton height={20} width={150} containerClassName='leading-none' />
          </div>
        </div>
      </div>
    </SkeletonTheme>
  );
};

export const CardListingFiltersSkeleton = () => {
  return (
    <SkeletonTheme baseColor="#f3f4f6" highlightColor="#ffffff">
      <div className="py-6">
        <div className="w-full flex justify-between items-center">
          <Skeleton height={20} width={80} />
          <div className="flex items-center gap-4">
            <Skeleton height={32} width={200} borderRadius={18} />
            <Skeleton height={32} width={86} borderRadius={18} />
          </div>
        </div>
      </div>
    </SkeletonTheme>
  );
};

export const RecommendationCardsSkeleton = () => {
  const isMaxScreen = window.innerWidth >= 1440;
  const cardsPerRow = isMaxScreen ? 5 : 4;
  const totalCards = cardsPerRow; // Only show 1 row

  return (
    <SkeletonTheme baseColor="#f3f4f6" highlightColor="#ffffff">
      <div className="mb-8">
        <div className="mb-6">
          <Skeleton height={32} width={240} borderRadius={8} />
        </div>
        <div className="grid grid-cols-4 max:grid-cols-5 gap-4 grid-rows-1">
          {Array.from({ length: totalCards }, (_, i) => (
            <CardSkeleton key={i} />
          ))}
        </div>
      </div>
    </SkeletonTheme>
  );
};

export const HotBrandsSkeleton = ({ title }) => {
  return (
    <SkeletonTheme baseColor="#f3f4f6" highlightColor="#ffffff">
      <div className="w-full">
        <h2 className="text-2xl font-bold text-[#111111] mb-6">{title}</h2>
        <div className="flex flex-col gap-1">
          {Array.from({ length: 3 }, (_, i) => (
            <BrandSkeleton key={i} />
          ))}
        </div>
      </div>
    </SkeletonTheme>
  );
};

export const HotCardsFiltersSkeleton = () => {
  return (
    <SkeletonTheme baseColor="#f3f4f6" highlightColor="#ffffff">
      <div className="flex justify-between items-center mb-8">
        <div className="flex items-center gap-4">
          <Skeleton height={32} width={192} borderRadius={6} />
          <Skeleton height={32} width={192} borderRadius={6} />
        </div>
        <Skeleton height={32} width={128} borderRadius={6} />
      </div>
    </SkeletonTheme>
  );
};

export const HotCardsSkeleton = ({ title }) => {
  const isMaxScreen = window.innerWidth >= 1440;
  const cardsPerRow = isMaxScreen ? 6 : 5;
  const totalCards = cardsPerRow * 5; // Show 2 rows initially

  return (
    <SkeletonTheme baseColor="#f3f4f6" highlightColor="#ffffff">
      <div className="mb-12">
        <h2 className="text-2xl font-bold text-[#111111] mb-4">{title}</h2>
        
        <HotCardsFiltersSkeleton />

        <div className="grid grid-cols-5 max:grid-cols-6 gap-6">
          {Array.from({ length: totalCards }, (_, i) => (
            <CardSkeleton key={i} />
          ))}
        </div>
      </div>
    </SkeletonTheme>
  );
};

export const SearchFiltersSkeleton = () => {
  return (
    <SkeletonTheme baseColor="#f3f4f6" highlightColor="#ffffff">
      <div className="flex justify-between items-center mb-8">
        <div className="flex items-center gap-4">
          <Skeleton height={32} width={192} borderRadius={18} />
          <Skeleton height={32} width={192} borderRadius={18} />
        </div>
        <div className="flex items-center gap-4">
          <Skeleton height={32} width={140} borderRadius={18} />
          <Skeleton height={32} width={290} borderRadius={18} />
        </div>
      </div>
    </SkeletonTheme>
  );
};

export const SearchResultsSkeleton = ({ showFilters = true }) => {
  const isMaxScreen = window.innerWidth >= 1440;
  const cardsPerRow = isMaxScreen ? 6 : 5;
  const totalCards = cardsPerRow * 6; // Show 3 rows initially

  return (
    <SkeletonTheme baseColor="#f3f4f6" highlightColor="#ffffff">
      <div>
        {/* Search results heading */}
        <div className="pt-6 pb-12 flex flex-col gap-[7px]">
          <Skeleton height={20} width={300} className="mb-2" />
        </div>

        {/* Filters */}
        {showFilters && <SearchFiltersSkeleton />}

        {/* Results grid */}
        <div className="grid grid-cols-5 max:grid-cols-6 gap-6">
          {Array.from({ length: totalCards }, (_, i) => (
            <CardSkeleton key={i} />
          ))}
        </div>
      </div>
    </SkeletonTheme>
  );
};

export const SearchRecommendedCardsSkeleton = () => {
  return (
    <SkeletonTheme baseColor="#f3f4f6" highlightColor="#ffffff">
      <div className="mt-8">
        <h2 className="text-xl font-bold mb-6">Recommended Cards</h2>
        <div className="grid grid-cols-5 gap-6">
          {Array.from({ length: 5 }, (_, i) => (
            <CardSkeleton key={i} />
          ))}
        </div>
      </div>
    </SkeletonTheme>
  );
};


export default Skeleton;